import React, { useState, useRef, useEffect } from 'react';
import { SearchOutlined, FilterOutlined, ReloadOutlined } from '@ant-design/icons';

/**
 * ProjectFilters
 * Search bar + filter controls: Project Type, Status, Department, Owner.
 * Matches the HTML design reference with pill-toggle for type and dropdowns for the rest.
 */

// ── Custom dropdown ───────────────────────────────────────────────────────────
const FilterDropdown = ({ label, value, options, onChange, placeholder, disabled = false }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const displayValue = value
        ? options.find(o => o.value === value)?.label || value
        : placeholder;

    return (
        <div className="flex flex-col gap-1.5" ref={ref}>
            <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">{label}</label>
            <div className="relative">
                <button
                    type="button"
                    disabled={disabled}
                    onClick={() => !disabled && setOpen(!open)}
                    className={`flex items-center justify-between gap-2 px-3.5 py-2.5 text-sm font-semibold border rounded-xl transition-all min-w-[150px] text-left ${disabled
                            ? 'bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 cursor-not-allowed opacity-60'
                            : 'bg-white dark:bg-black border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-900'
                        }`}
                    style={{ color: disabled ? '#94a3b8' : (value ? '#0f172a' : '#94a3b8') }}
                >
                    <span className="truncate dark:text-slate-300">{displayValue}</span>
                    <span className="material-symbols-outlined text-base text-slate-400">expand_more</span>
                </button>
                {open && !disabled && (
                    <div className="absolute top-full left-0 mt-1 w-full min-w-[190px] bg-white dark:bg-[#111] border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl z-50 max-h-60 overflow-y-auto py-1">
                        <button
                            type="button"
                            onClick={() => { onChange(null); setOpen(false); }}
                            className={`w-full px-3.5 py-2 text-left text-sm font-semibold transition-colors ${!value ? 'text-blue-600 bg-blue-50 dark:bg-blue-950' : 'text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-900'}`}
                        >
                            {placeholder}
                        </button>
                        {options.map(opt => (
                            <button
                                key={opt.value}
                                type="button"
                                onClick={() => { onChange(opt.value); setOpen(false); }}
                                className={`w-full px-3.5 py-2 text-left text-sm font-semibold transition-colors ${value === opt.value ? 'text-blue-600 bg-blue-50 dark:bg-blue-950' : 'text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-900'}`}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

const CATEGORIES_MAP = {
    'Amazon & Ecommerce': ['Listing Design', 'Store Management', 'Amazon Store Setup', 'Product Research', 'PPC & Advertising', 'A+ Content Design'],
    'Website': ['Website Development', 'Website Maintenance', 'Website Redesign', 'UI/UX Design', 'CMS Integration', 'E-Commerce Website'],
    'Graphic Design': ['Logo Design', 'Stationery Design', 'Brochure Design', 'Social Media Design', 'Brand Identity', 'Packaging Design'],
    'Mobile App': ['iOS App Development', 'Android App Development', 'Cross-Platform App', 'Mobile App Maintenance', 'App UI/UX Design'],
    'Video Animation': ['2D Explainer Video', '3D Animation Video', 'Video Editing', 'Motion Graphics', 'Logo Intro Animation'],
    'Marketing': ['Social Media Marketing', 'SEO Optimization', 'Content Marketing', 'Email Marketing', 'PPC Campaign Management'],
};

export default function ProjectFilters({
    filters,
    onFilterChange,
    employees = [],
    departments = [],
}) {
    const {
        search = '',
        type = 'All',
        category = null,
        subCategory = null,
        duration = null,
        status = null,
        department = null,
        owner = null,
    } = filters;

    const update = (key, value) => {
        onFilterChange({ ...filters, [key]: value });
    };

    const resetAll = () => {
        onFilterChange({
            search: '',
            type: 'All',
            category: null,
            subCategory: null,
            duration: null,
            status: null,
            department: null,
            owner: null,
        });
    };

    const typeOptions = ['All', 'Client', 'In-house'];

    const categoryOptions = Object.keys(CATEGORIES_MAP).map(cat => ({
        value: cat,
        label: cat,
    }));

    const subCategoryOptions = (category && CATEGORIES_MAP[category]
        ? CATEGORIES_MAP[category]
        : []
    ).map(sub => ({
        value: sub,
        label: sub,
    }));

    const durationOptions = [
        { value: 'Fixed', label: 'Fixed Duration' },
        { value: 'Ongoing', label: 'Ongoing' },
    ];

    const statusOptions = [
        { value: 'Active', label: 'Active' },
        { value: 'In Progress', label: 'In Progress' },
        { value: 'Draft', label: 'Draft' },
        { value: 'Backlog / Upcoming', label: 'Backlog / Upcoming' },
        { value: 'On Hold', label: 'On Hold' },
        { value: 'Completed', label: 'Completed' },
        { value: 'Closed', label: 'Closed' },
        { value: 'Not Active', label: 'Not Active' },
        { value: 'Cancelled', label: 'Cancelled' },
        { value: 'Archived', label: 'Archived' },
    ];

    const departmentOptions = departments.map(d => ({
        value: d,
        label: d,
    }));

    const ownerOptions = employees.map(emp => ({
        value: emp._id,
        label: emp.name,
    }));

    const hasActiveFilters = search || type !== 'All' || category || subCategory || duration || status || department || owner;

    return (
        <div
            className="bg-white dark:bg-black p-4 rounded-2xl border border-slate-200 dark:border-slate-800 flex flex-wrap items-center gap-4"
            style={{ boxShadow: '0px 2px 8px rgba(25, 28, 29, 0.04)' }}
        >
            <div className="flex flex-col lg:flex-row items-start lg:items-end gap-4 w-full">
                {/* Search */}
                <div className="flex-1 w-full lg:w-auto flex flex-col gap-1.5">
                    <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Search</label>
                    <div className="relative">
                        <SearchOutlined className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" style={{ fontSize: 16 }} />
                        <input
                            className="w-full bg-slate-50/50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 pl-10 pr-4 py-2.5 rounded-xl text-sm focus:ring-2 focus:ring-blue-400 dark:focus:ring-blue-600 transition-all outline-none text-slate-800 dark:text-slate-200 placeholder-slate-400"
                            placeholder="Search projects, categories, clients, owners..."
                            value={search}
                            onChange={(e) => update('search', e.target.value)}
                        />
                    </div>
                </div>

                {/* Filters row */}
                <div className="flex flex-wrap items-end gap-3">
                    {/* Type toggle pills */}
                    <div className="flex flex-col gap-1.5">
                        <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Project Type</label>
                        <div className="flex bg-slate-100 dark:bg-slate-900 p-1 rounded-xl">
                            {typeOptions.map(t => (
                                <button
                                    key={t}
                                    type="button"
                                    onClick={() => update('type', t)}
                                    className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all ${type === t
                                        ? 'bg-white dark:bg-slate-800 text-[#003263] dark:text-[#a7c8ff] shadow-sm'
                                        : 'text-slate-500 dark:text-slate-400 hover:text-[#003263] dark:hover:text-white'
                                        }`}
                                >
                                    {t}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Category */}
                    <FilterDropdown
                        label="Category"
                        value={category}
                        options={categoryOptions}
                        onChange={(v) => {
                            onFilterChange({ ...filters, category: v, subCategory: null });
                        }}
                        placeholder="All Categories"
                    />

                    {/* Sub-Category */}
                    <FilterDropdown
                        label="Sub-Category"
                        value={subCategory}
                        options={subCategoryOptions}
                        onChange={(v) => update('subCategory', v)}
                        placeholder={category ? "All Sub-Categories" : "Select Category First"}
                        disabled={!category}
                    />

                    {/* Duration */}
                    <FilterDropdown
                        label="Duration"
                        value={duration}
                        options={durationOptions}
                        onChange={(v) => update('duration', v)}
                        placeholder="All Durations"
                    />

                    {/* Status */}
                    <FilterDropdown
                        label="Status"
                        value={status}
                        options={statusOptions}
                        onChange={(v) => update('status', v)}
                        placeholder="All Statuses"
                    />

                    {/* Department */}
                    <FilterDropdown
                        label="Department"
                        value={department}
                        options={departmentOptions}
                        onChange={(v) => update('department', v)}
                        placeholder="All Departments"
                    />

                    {/* Owner */}
                    <FilterDropdown
                        label="Owner"
                        value={owner}
                        options={ownerOptions}
                        onChange={(v) => update('owner', v)}
                        placeholder="Select Owner"
                    />

                    {/* Reset */}
                    {hasActiveFilters && (
                        <button
                            onClick={resetAll}
                            className="flex items-center gap-1 text-xs font-bold text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 transition-colors pb-3 ml-1"
                        >
                            <ReloadOutlined style={{ fontSize: 13 }} /> Reset
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
